import { useEffect, useState } from "react";
import type { FamilyStudentDetailDto, ProfilePhotoDto, ViewerMemberCommunicationDto, ViewerMemberCommunicationsResponseDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { fetchFamilyStudentDetail } from "../../shared/api/family";
import { fetchFamilyMemberCommunications } from "../../shared/api/memberCommunication";
import { fetchStudentProfilePhoto, setStudentProfilePhoto } from "../../shared/api/profilePhoto";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { DEFAULT_TENANT_TIMEZONE } from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { canUseFamilyMode } from "../../shared/session/modeRouting";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { FamilyAppShell } from "../../shared/ui/family/FamilyAppShell";
import {
  FamilyScheduleItemCard,
  PersonAvatar,
  formatFamilyDate,
  formatFamilyDateTime
} from "./familyUi";
import { EditableAvatar } from "../../shared/ui/profile/EditableAvatar";
import { CircularAvatarEditorDialog } from "../../shared/ui/profile/CircularAvatarEditorDialog";
import { MemberCommunicationCard } from "../../shared/ui/MemberCommunicationCard";

function buildOccurrenceAbsencePath(
  tenantSlug: string,
  item: FamilyStudentDetailDto["upcomingItems"][number]
): string | null {
  return item.itemType === "occurrence" ? `/t/${tenantSlug}/occurrences/${item.itemId}/absence` : null;
}

export function StudentDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", studentId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [studentDetail, setStudentDetail] = useState<FamilyStudentDetailDto | null>(null);
  const [communications, setCommunications] = useState<ViewerMemberCommunicationsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<ProfilePhotoDto | null>(null);
  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const [photoNotice, setPhotoNotice] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const familyTimeZone = DEFAULT_TENANT_TIMEZONE;
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole || !studentId) {
      return;
    }

    let cancelled = false;

    async function loadStudent() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [nextStudentDetail, communicationsResult, photoResult] = await Promise.all([
          fetchFamilyStudentDetail(tenantSlug, studentId),
          fetchFamilyMemberCommunications(tenantSlug).then((value) => ({ status: "fulfilled" as const, value })).catch((reason) => ({
            status: "rejected" as const,
            reason
          })),
          fetchStudentProfilePhoto(tenantSlug, studentId).catch(() => null)
        ]);

        if (cancelled) {
          return;
        }

        setStudentDetail(nextStudentDetail);
        setProfilePhoto(photoResult?.profilePhoto ?? nextStudentDetail.student.profilePhoto);
        setCommunications(communicationsResult.status === "fulfilled" ? communicationsResult.value : { items: [], unreadCount: 0 });
        if (communicationsResult.status === "rejected") {
          setLoadError(`関連する連絡事項の取得に失敗しました。${tenantLabels.student}情報と予定は表示しています。`);
        }
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        if (error instanceof ApiError && error.status === 404) {
          setLoadState("error");
          setLoadError(`この${tenantLabels.student}は現在の保護者アカウントから参照できません。紐付け状況を確認してください。`);
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.student}詳細の取得に失敗しました`));
      }
    }

    void loadStudent();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, studentId, tenantLabels.student, tenantSlug]);

  function isRelevantCommunication(communication: ViewerMemberCommunicationDto): boolean {
    if (!studentDetail) {
      return false;
    }
    if (communication.targetType === "tenant") {
      return true;
    }
    if (communication.targetType === "lesson") {
      return studentDetail.enrollments.some((enrollment) => enrollment.lessonId === communication.targetId && enrollment.status === "active");
    }
    return studentDetail.upcomingItems.some((item) => item.itemId === communication.targetId);
  }

  const relatedCommunications = communications?.items.filter(isRelevantCommunication).slice(0, 6) ?? [];

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="home"
      navigationCountsEnabled={hasFamilyAccessRole}
    >
      <div className="family-page-stack">
        {routeStatus === "checking" || sessionStatus === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">セッションを確認しています。</p>
          </article>
        ) : null}
        {routeStatus === "redirecting" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">利用開始へ移動しています。</p>
          </article>
        ) : null}
        {sessionStatus === "error" ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">セッション確認に失敗しました。{errorMessage}</p>
          </article>
        ) : null}
        {routeStatus === "authenticated" && !hasFamilyAccessRole ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">
              保護者または{tenantLabels.student}本人としての利用権限が必要です。保護者の方は
              {" "}
              <Link to={`/t/${tenantSlug}/link-student`}>{tenantLabels.student}連携</Link>
              {" "}
              へ進み、{tenantLabels.student}本人は本人用招待をご利用ください。
            </p>
          </article>
        ) : null}

        <section className="family-surface family-section">
          <h2 className="family-page-title">{tenantLabels.student}詳細</h2>
          <p className="family-page-intro">
            連携中の{tenantLabels.student}の情報、{tenantLabels.enrollment}中の{tenantLabels.lesson}、今後の予定を表示します。
          </p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">{tenantLabels.student}詳細を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {studentDetail ? (
          <>
            <section className="family-surface family-section" style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <EditableAvatar displayName={studentDetail.student.displayName} photoUrl={profilePhoto?.downloadUrl ?? null} size={104} editable onEdit={() => setPhotoEditorOpen(true)} />
                <strong style={{ fontSize: 24 }}>{studentDetail.student.displayName}</strong>
              </div>
              <div className="family-text-muted">かな: {studentDetail.student.displayNameKana ?? "未設定"}</div>
              <div className="family-text-muted">生年月日: {studentDetail.student.birthDate ?? "未設定"}</div>
              <div className="family-text-muted">ステータス: {describeStatus(studentDetail.student.status)}</div>
              <div className="family-text-muted">
                次回予定: {studentDetail.student.nextScheduledAt ? formatFamilyDateTime(studentDetail.student.nextScheduledAt, familyTimeZone) : "なし"}
              </div>
              <div className="family-text-muted">予定件数: {studentDetail.student.upcomingCount}</div>
            </section>

            <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 20 }}>{tenantLabels.enrollment}中の{tenantLabels.lesson}</h3>
              {studentDetail.enrollments.length === 0 ? <p className="family-empty-state">現在表示できる{tenantLabels.enrollment}情報はありません。</p> : null}
              {studentDetail.enrollments.map((enrollment) => (
                <article
                  key={enrollment.enrollmentId}
                  className="family-list-card"
                >
                  <strong>{enrollment.lessonTitle}</strong>
                  <div className="family-text-muted">
                    {describeStatus(enrollment.enrollmentType)} / {describeStatus(enrollment.status)}
                  </div>
                  <div className="family-text-muted">
                    参加期間: {formatFamilyDate(enrollment.joinDate, familyTimeZone)} 〜 {enrollment.leaveDate ? formatFamilyDate(enrollment.leaveDate, familyTimeZone) : "継続中"}
                  </div>
                </article>
              ))}
            </section>

            <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 20 }}>今後の予定</h3>
              {studentDetail.upcomingItems.length === 0 ? (
                <p className="family-empty-state">
                  表示できる予定はありません。スケジュールが未公開、または所属レッスンに予定がありません。
                </p>
              ) : null}
              <div className="family-list family-list--compact">
                {studentDetail.upcomingItems.map((item) => (
                  <FamilyScheduleItemCard
                    key={`${item.itemType}:${item.itemId}`}
                    item={item}
                    showStudentBadges={false}
                    to={buildOccurrenceAbsencePath(tenantSlug, item)}
                    timeZone={familyTimeZone}
                    labels={tenantLabels}
                  />
                ))}
              </div>
            </section>

            <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
              <div className="family-section__header">
                <div>
                  <h3 style={{ margin: 0, fontSize: 20 }}>関連する連絡事項</h3>
                </div>
                <Link className="family-section__action" to={`/t/${tenantSlug}/member-communications`}>すべて見る</Link>
              </div>
              {relatedCommunications.length === 0 ? <p className="family-empty-state">この{tenantLabels.student}に関連する連絡事項はまだありません。</p> : null}
              {relatedCommunications.length > 0 ? (
                <div className="family-list family-list--compact">
                  {relatedCommunications.map((communication) => (
                    <MemberCommunicationCard
                      key={communication.communicationId}
                      communication={communication}
                      to={`/t/${tenantSlug}/member-communications/${communication.communicationId}`}
                      timeZone={familyTimeZone}
                      tenantSlug={tenantSlug}
                      thumbnailSurface="family"
                      enableFamilyAttachmentOpen
                      compact
                    />
                  ))}
                </div>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
      {photoNotice ? <div className="family-photo-notice" role="status">{photoNotice}</div> : null}
      <CircularAvatarEditorDialog
        open={photoEditorOpen}
        onCancel={() => setPhotoEditorOpen(false)}
        onSet={async (blob) => {
          if (blob.size > 2 * 1024 * 1024) throw new Error("画像の処理に失敗しました。別の画像を選択してください。");
          const photo = await setStudentProfilePhoto(tenantSlug, studentId, blob);
          setProfilePhoto(photo);
          setPhotoNotice("プロフィール写真を設定しました。");
        }}
      />
    </FamilyAppShell>
  );
}
